import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useContactTasks(params?: {
  page?: number;
  limit?: number;
  search?: string;
  contact_id?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
}) {
  return useQuery({
    queryKey: ["contact-tasks", params],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase.from("contact_tasks").select(`
                    *,
                    contacts:contact_id (
                        id,
                        name,
                        company_name,
                        email
                    ),
                    users:app_users(
                        id,
                        first_name,
                        last_name,
                        email
                    ),
                    assigned_user:assigned_to (
                        id,
                        first_name,
                        last_name,
                        email
                    )
                `);

      if (params?.contact_id) {
        query = query.eq("contact_id", params.contact_id);
      }
      if (params?.status) {
        query = query.eq("status", params.status);
      }
      if (params?.priority) {
        query = query.eq("priority", params.priority);
      }
      if (params?.assigned_to) {
        query = query.eq("assigned_to", params.assigned_to);
      }
      if (params?.search) {
        query = query.or(`title.ilike.%${params.search}%,description.ilike.%${params.search}%`);
      }

      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        query = query.range(offset, offset + params.limit - 1);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        const message =
          (error as { message?: string }).message ||
          (error as { details?: string }).details ||
          (error as { code?: string }).code ||
          "Failed to fetch contact tasks";
        if (process.env.NODE_ENV === "development") {
          console.warn("Contact tasks query error:", message, error);
        }
        throw new Error(message);
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactTask(id: string) {
  return useQuery({
    queryKey: ["contact-tasks", id],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_tasks")
        .select(
          `
                    *,
                    contacts:contact_id (
                        id,
                        name,
                        company_name,
                        email
                    ),
                    users:app_users(
                        id,
                        first_name,
                        last_name,
                        email
                    ),
                    assigned_user:assigned_to (
                        id,
                        first_name,
                        last_name,
                        email
                    )
                `
        )
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching contact task:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}
